import React, { useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged, signOut, sendEmailVerification } from "firebase/auth";
import { 
  doc, 
  setDoc, 
  getDoc,
  onSnapshot, 
  collection, 
  query, 
  where,
  orderBy, 
  updateDoc, 
  serverTimestamp,
  writeBatch,
  increment
} from "firebase/firestore";
import { auth, db } from "./firebaseConfig";
import { User, AccountType, RobotState, Trade, Deal, SystemSettings } from './types';
import { CURRENCY_PAIRS, Icons } from './constants';
import Layout from './components/Layout';
import ChartWidget from './components/ChartWidget';
import RobotSetup from './pages/RobotSetup';
import ActiveRobot from './pages/ActiveRobot';
import Deals from './pages/Deals';
import Support from './pages/Support';
import Account from './pages/Account';
import AdminPanel from './pages/AdminPanel';
import LandingPage from './components/LandingPage';
import Auth from './components/Auth';
import DepositModal from './components/DepositModal';
import AboutUs from './pages/AboutUs';

const SESSION_DURATION = 24 * 60 * 60 * 1000;
const TRADE_DURATION = 5 * 60 * 1000; 
const SEARCH_DURATION = 5000; 
const CYCLE_DURATION = TRADE_DURATION + SEARCH_DURATION; 

const WIN_RATE = 0.70; 
const MAX_PROFIT_FACTOR = 0.015; 
const MAX_LOSS_FACTOR = 0.02; 

const ADMIN_EMAIL = 'aloysiusebuka1993@gmail.com';
const ADMIN_UID = 'ocaVvAyxvYhsLstIHnfNrYiz8nc2';

const App: React.FC = () => {
  const [firebaseUser, setFirebaseUser] = useState<{ uid: string, email: string | null, emailVerified: boolean, displayName: string | null } | null>(null);
  const [publicPage, setPublicPage] = useState<'landing' | 'about'>('landing');
  const [authModal, setAuthModal] = useState<boolean>(false);
  const [depositModal, setDepositModal] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [selectedPair, setSelectedPair] = useState<string>('BTC/USDT');
  const [totalNetworkPayout, setTotalNetworkPayout] = useState<number>(0);
  const deferredPromptRef = useRef<any>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'system', 'settings'), (snap) => {
      if (snap.exists()) setSystemSettings(snap.data() as SystemSettings);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if ("Notification" in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Check if already in standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone;

    if (!isStandalone) {
      // Show banner on every load if not installed
      setShowInstallBanner(true);
    }

    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      // Banner is already shown by default on load, but this ensures we have the prompt
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPromptRef.current) {
      // If no prompt available (e.g. user already dismissed it or browser doesn't support it)
      // we can't do much on Android except show a generic message
      alert("To install: Open your browser menu (three dots) and select 'Install' or 'Add to Home Screen'.");
      return;
    }

    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }
    deferredPromptRef.current = null;
    setShowInstallBanner(false);
  };

  useEffect(() => {
    if (systemSettings?.siteName) {
      document.title = `${systemSettings.siteName} - AI Robot Trading`;
    } else {
      document.title = `BTCC-AI Trading - AI Robot Trading`;
    }
  }, [systemSettings]);

  const [user, setUser] = useState<User>({
    id: '1',
    fullName: 'Trader',
    email: '',
    demoBalance: 10000.00,
    liveBalance: 0.00,
    registrationDate: new Date().toISOString(),
    country: 'Global',
    uid: '',
    isVerified: false,
    botActive: false,
    accountType: AccountType.DEMO,
    role: 'user',
    status: 'active'
  });

  const userRef = useRef<User>(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const [activeAccountType, setActiveAccountType] = useState<AccountType>(AccountType.DEMO);
  const [activePage, setActivePage] = useState<string>('chart');
  const [accountInitialView, setAccountInitialView] = useState<any>(undefined);
  const [robotState, setRobotState] = useState<RobotState & { lastPair?: string }>({
    isActive: false,
    startTime: null,
    tradeAmount: 0,
    currentTrade: null,
    isSearching: false,
    totalSessionProfit: 0,
    unrealizedProfit: 0,
    accountType: AccountType.DEMO,
    lastPair: undefined
  });
  const [allTradesRaw, setAllTradesRaw] = useState<Trade[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);

  const unsubDocRef = useRef<(() => void) | null>(null);
  const unsubTradesRef = useRef<(() => void) | null>(null);
  const unsubSettingsRef = useRef<(() => void) | null>(null);
  const unsubPayoutRef = useRef<(() => void) | null>(null);
  const engineTimerRef = useRef<any>(null);
  const isReconcilingRef = useRef<boolean>(false);
  const hasSyncedAccountRef = useRef<boolean>(false);

  const cleanupListeners = useCallback(() => {
    if (unsubDocRef.current) unsubDocRef.current();
    if (unsubTradesRef.current) unsubTradesRef.current();
    if (unsubSettingsRef.current) unsubSettingsRef.current();
    if (unsubPayoutRef.current) unsubPayoutRef.current();
    unsubDocRef.current = null;
    unsubTradesRef.current = null;
    unsubSettingsRef.current = null;
    unsubPayoutRef.current = null;
    if (engineTimerRef.current) clearInterval(engineTimerRef.current);
    hasSyncedAccountRef.current = false;
  }, []);

  const reconcileOfflineTrades = async (userData: User, forceTrade: Trade | null = null, forceProfit: number | null = null) => {
    if (!userData.botActive || !userData.botStartTime || !userData.lastReconciliationTime || isReconcilingRef.current) return;
    
    const now = Date.now();
    const sessionEndTime = userData.botStartTime + SESSION_DURATION;
    const effectiveNow = Math.min(now, sessionEndTime);
    
    const timeDiff = effectiveNow - userData.lastReconciliationTime;
    if (timeDiff < CYCLE_DURATION && forceTrade === null) return; 

    isReconcilingRef.current = true;
    const numCycles = Math.floor(timeDiff / CYCLE_DURATION);
    const tradeAmount = userData.botTradeAmount || 100;
    const accountType = userData.accountType;
    
    let totalProfitGenerated = 0;
    const tradesToAdd: Trade[] = [];

    for (let i = 0; i < (numCycles > 0 ? numCycles : (forceTrade ? 1 : 0)); i++) {
      const cycleStartTime = userData.lastReconciliationTime + (i * CYCLE_DURATION);
      
      let finalProfit: number;
      let tradePair: string;
      let tradeType: 'BUY' | 'SELL';
      let broker: string;

      if (i === 0 && forceTrade && forceProfit !== null) {
        finalProfit = forceProfit;
        tradePair = forceTrade.pair;
        tradeType = forceTrade.type;
        broker = forceTrade.broker;
      } else {
        const randomPair = CURRENCY_PAIRS[Math.floor(Math.random() * CURRENCY_PAIRS.length)];
        tradePair = randomPair.name;
        tradeType = Math.random() > 0.5 ? 'BUY' : 'SELL';
        broker = randomPair.broker;

        const effectiveWinRate = (userData.winRate && userData.winRate > 0) ? userData.winRate : WIN_RATE;
        const isWin = Math.random() < effectiveWinRate;
        if (isWin) {
          finalProfit = parseFloat((Math.random() * (tradeAmount * MAX_PROFIT_FACTOR - 0.5) + 0.5).toFixed(2));
        } else {
          finalProfit = -parseFloat((Math.random() * (tradeAmount * MAX_LOSS_FACTOR - 0.5) + 0.5).toFixed(2));
        }
      }
      
      totalProfitGenerated += finalProfit;
      
      tradesToAdd.push({
        id: `settled-${cycleStartTime}-${i}-${Math.random().toString(36).slice(2, 5)}`,
        pair: tradePair,
        type: tradeType,
        amount: tradeAmount,
        profit: finalProfit,
        result: finalProfit >= 0 ? 'win' : 'loss',
        timestamp: cycleStartTime + SEARCH_DURATION,
        broker: broker,
        status: 'completed',
        accountType: accountType,
        userId: userData.uid,
        userName: userData.fullName,
        userEmail: userData.email
      });
    }

    if (tradesToAdd.length === 0) {
      isReconcilingRef.current = false;
      return;
    }

    try {
      const batch = writeBatch(db);
      const userDocRef = doc(db, 'users', userData.uid);
      const balanceKey = accountType === AccountType.DEMO ? 'demoBalance' : 'liveBalance';
      
      const processedTime = numCycles > 0 ? (numCycles * CYCLE_DURATION) : (forceTrade ? (effectiveNow - userData.lastReconciliationTime) : 0);
      const newLastRecon = userData.lastReconciliationTime + processedTime;
      const isSessionOver = effectiveNow >= sessionEndTime;

      batch.update(userDocRef, {
        [balanceKey]: increment(totalProfitGenerated),
        lastReconciliationTime: newLastRecon,
        botActive: !isSessionOver
      });

      const tradesCollectionRef = collection(db, 'users', userData.uid, 'trades');
      const globalTradesRef = collection(db, 'trades');
      
      tradesToAdd.forEach(t => {
        const tRef = doc(tradesCollectionRef);
        const gRef = doc(globalTradesRef, tRef.id);
        const dataWithStamp = { ...t, createdAt: serverTimestamp() };
        batch.set(tRef, dataWithStamp);
        batch.set(gRef, dataWithStamp);
      });

      await batch.commit();

      // Notify user of trade results
      if ("Notification" in window && Notification.permission === "granted") {
        tradesToAdd.forEach(trade => {
          const result = trade.profit >= 0 ? "WIN" : "LOSS";
          const title = `Trade Result: ${result}`;
          const body = `${trade.pair} ${trade.type} - Profit: $${trade.profit.toFixed(2)}`;
          new Notification(title, {
            body,
            icon: "/icon.svg"
          });
        });
      }
    } catch (err) {
      // Silently fail
    } finally {
      isReconcilingRef.current = false;
    }
  };

  const handleStopRobot = useCallback(async () => {
    if (!auth.currentUser) return;
    
    if (robotState.currentTrade) {
      await reconcileOfflineTrades(userRef.current, robotState.currentTrade, robotState.targetProfit || 0);
    }

    try {
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userDocRef, { 
        botActive: false,
        botStartTime: null,
        lastReconciliationTime: null 
      });
    } catch (err: any) {
      console.error("Robot Stop Error:", err.message);
    }

    setRobotState(prev => ({
      ...prev,
      isActive: false,
      startTime: null,
      currentTrade: null,
      isSearching: false,
      unrealizedProfit: 0,
      totalSessionProfit: 0,
      lastPair: undefined
    }));
  }, [robotState.currentTrade, robotState.unrealizedProfit, robotState.targetProfit]);

  useEffect(() => {
    if (user.botActive && user.botStartTime) {
      const sessionTrades = allTradesRaw.filter(t => 
        t.accountType === user.accountType && 
        t.timestamp >= (user.botStartTime || 0)
      );
      const sessionProfit = sessionTrades.reduce((acc, t) => acc + (t.profit || 0), 0);
      setRobotState(prev => ({ ...prev, totalSessionProfit: sessionProfit }));
    } else {
      setRobotState(prev => ({ ...prev, totalSessionProfit: 0 }));
    }
  }, [allTradesRaw, user.botActive, user.botStartTime, user.accountType]);

  useEffect(() => {
    if (!user.botActive || !user.botStartTime || !user.uid) {
      if (engineTimerRef.current) clearInterval(engineTimerRef.current);
      return;
    }

    const runEngine = () => {
      const now = Date.now();
      const currentUser = userRef.current;
      const elapsed = now - (currentUser.botStartTime || 0);

      if (elapsed >= SESSION_DURATION) {
        handleStopRobot();
        return;
      }

      const cycleIndex = Math.floor(elapsed / CYCLE_DURATION);
      const phaseElapsed = elapsed % CYCLE_DURATION;
      const isSearching = phaseElapsed < SEARCH_DURATION;

      setRobotState(prev => {
        const newState = { 
          ...prev, 
          isActive: true, 
          isSearching, 
          startTime: currentUser.botStartTime!, 
          tradeAmount: currentUser.botTradeAmount || 100, 
          accountType: currentUser.accountType 
        };

        if (isSearching) {
          if (prev.currentTrade && prev.currentTrade.status === 'active') {
             reconcileOfflineTrades(currentUser, prev.currentTrade, prev.targetProfit || 0);
          }
          return { ...newState, currentTrade: null, unrealizedProfit: 0, targetProfit: undefined };
        } else {
          const tradeProgress = (phaseElapsed - SEARCH_DURATION) / TRADE_DURATION;
          
          if (!prev.currentTrade || prev.currentTrade.id !== `cycle-${cycleIndex}`) {
            const randomPair = CURRENCY_PAIRS[Math.floor(Math.random() * CURRENCY_PAIRS.length)];
            const effectiveWinRate = (currentUser.winRate && currentUser.winRate > 0) ? currentUser.winRate : WIN_RATE;
            const isWin = Math.random() < effectiveWinRate;
            const target = isWin 
              ? parseFloat((Math.random() * (newState.tradeAmount * MAX_PROFIT_FACTOR - 0.5) + 0.5).toFixed(2))
              : -parseFloat((Math.random() * (newState.tradeAmount * MAX_LOSS_FACTOR - 0.5) + 0.5).toFixed(2));

            newState.currentTrade = {
              id: `cycle-${cycleIndex}`,
              pair: randomPair.name,
              type: Math.random() > 0.5 ? 'BUY' : 'SELL',
              amount: newState.tradeAmount,
              profit: target,
              result: target >= 0 ? 'win' : 'loss',
              timestamp: now - phaseElapsed + SEARCH_DURATION,
              broker: randomPair.broker,
              status: 'active',
              accountType: currentUser.accountType
            };
            newState.targetProfit = target;
            newState.lastPair = randomPair.name; // Remember pair for chart persistence
          }

          if (newState.targetProfit !== undefined) {
            const noise = (Math.random() - 0.5) * (newState.tradeAmount * 0.0005);
            const linearProfit = newState.targetProfit * tradeProgress;
            newState.unrealizedProfit = linearProfit + noise;
          }

          return newState;
        }
      });
    };

    engineTimerRef.current = setInterval(runEngine, 1000);
    return () => {
      if (engineTimerRef.current) clearInterval(engineTimerRef.current);
    };
  }, [user.botActive, user.botStartTime, user.uid, user.accountType, handleStopRobot]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (u) => {
      cleanupListeners();
      if (u) {
        setFirebaseUser({
          uid: u.uid,
          email: u.email,
          emailVerified: u.emailVerified,
          displayName: u.displayName
        });
      } else {
        setFirebaseUser(null);
      }

      if (u) {
        const userDocRef = doc(db, 'users', u.uid);

        try {
          const userSnap = await getDoc(userDocRef);
          if (!userSnap.exists()) {
            const isTargetAdmin = u.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase() || u.uid === ADMIN_UID;
            await setDoc(userDocRef, {
              fullName: u.displayName || 'Trader',
              email: u.email || '',
              demoBalance: 10000.00,
              liveBalance: 0.00,
              createdAt: serverTimestamp(),
              registrationDate: new Date().toISOString(),
              uid: u.uid,
              isVerified: false,
              botActive: false,
              accountType: AccountType.DEMO,
              role: isTargetAdmin ? 'admin' : 'user',
              status: 'active',
              referralCode: u.uid.slice(0, 8).toUpperCase()
            });
          }
        } catch (err) {
          console.error("Initial user sync failed", err);
        }

        unsubDocRef.current = onSnapshot(userDocRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as User;
            setUser(prev => ({ ...prev, ...data, id: u.uid }));
            
            if (!hasSyncedAccountRef.current && data.botActive) {
               setActiveAccountType(data.accountType);
               hasSyncedAccountRef.current = true;
            }

            if (data.role === 'admin' && isInitializing) setActivePage('admin');
            
            if (data.botActive && !isReconcilingRef.current) reconcileOfflineTrades(data);
          }
        });

        // Global Payout Listener
        const withdrawalsRef = collection(db, 'withdrawals');
        const qPayouts = query(withdrawalsRef, where('status', '==', 'confirmed'));
        unsubPayoutRef.current = onSnapshot(qPayouts, (snapshot) => {
           const total = snapshot.docs.reduce((acc, d) => acc + (d.data().amount || 0), 0);
           setTotalNetworkPayout(total);
        });

        const tradesRef = collection(db, 'users', u.uid, 'trades');
        const qTrades = query(tradesRef, orderBy('timestamp', 'desc'));
        unsubTradesRef.current = onSnapshot(qTrades, (snapshot) => {
          const allTrades: Trade[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Trade));
          setAllTradesRaw(allTrades);
          
          const grouped: Record<string, Deal> = {};
          allTrades.forEach(trade => {
            const d = new Date(trade.timestamp);
            const stableDate = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
            const groupKey = `${stableDate}_${trade.accountType}`;
            if (!grouped[groupKey]) {
              grouped[groupKey] = { date: stableDate, totalProfit: 0, tradesCount: 0, trades: [], accountType: trade.accountType };
            }
            grouped[groupKey].totalProfit += (trade.profit || 0);
            grouped[groupKey].tradesCount += 1;
            grouped[groupKey].trades.push(trade);
          });
          setDeals(Object.values(grouped).sort((a, b) => b.date.localeCompare(a.date)));
        });
      } else {
        // Even if logged out, we should listen to system settings for branding on Landing Page
      }
      setIsInitializing(false);
    });

    return () => {
      unsubscribeAuth();
      cleanupListeners();
    };
  }, [cleanupListeners, isInitializing]);

  const handleCheckVerification = async () => {
    if (auth.currentUser) {
      try {
        await auth.currentUser.reload();
        const updatedUser = auth.currentUser;
        setFirebaseUser({
          uid: updatedUser.uid,
          email: updatedUser.email,
          emailVerified: updatedUser.emailVerified,
          displayName: updatedUser.displayName
        });
        
        if (updatedUser.emailVerified) {
          // Sync to Firestore
          const userDocRef = doc(db, 'users', updatedUser.uid);
          await updateDoc(userDocRef, { isVerified: true });
        }
      } catch (err) {
        console.error("Verification check failed", err);
      }
    }
  };

  const handleResendEmail = async () => {
    if (auth.currentUser) {
      setResendingEmail(true);
      try {
        await sendEmailVerification(auth.currentUser);
        alert("Verification email sent. Please check your inbox.");
      } catch (err: any) {
        alert("Verification resend failed: " + err.message);
      } finally {
        setResendingEmail(false);
      }
    }
  };

  const handleStartRobot = async (amount: number, accountType: AccountType) => {
    if (!firebaseUser) return;
    if (user.status === 'suspended') return alert("Your account is currently suspended.");
    const balance = accountType === AccountType.DEMO ? user.demoBalance : user.liveBalance;
    if (amount > balance) return alert("Insufficient balance.");

    try {
      const now = Date.now();
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const batch = writeBatch(db);

      const randomPair = CURRENCY_PAIRS[Math.floor(Math.random() * CURRENCY_PAIRS.length)].name;
      setSelectedPair(randomPair);

      batch.update(userDocRef, { 
        botActive: true, accountType, botStartTime: now, lastReconciliationTime: now, botTradeAmount: amount
      });

      if (accountType === AccountType.LIVE && user.referredBy && systemSettings?.referralPercent) {
        const bonus = amount * (systemSettings.referralPercent / 100);
        const referrerRef = doc(db, 'users', user.referredBy);
        batch.update(referrerRef, { liveBalance: increment(bonus) });
        const refTxId = `REF-BONUS-${user.uid.slice(0, 5)}-${now}`;
        const refTxData = { 
          id: refTxId, 
          userId: user.referredBy, 
          userName: 'Referral Reward', 
          amount: bonus, 
          currency: 'USDT', 
          status: 'confirmed', 
          network: 'INTERNAL', 
          detail: 'referral',
          createdAt: serverTimestamp() 
        };
        batch.set(doc(db, 'users', user.referredBy, 'deposits', refTxId), refTxData);
        batch.set(doc(db, 'deposits', refTxId), refTxData);
      }
      await batch.commit();
      
      setRobotState({
        isActive: true, startTime: now, tradeAmount: amount, currentTrade: null, isSearching: true, totalSessionProfit: 0, unrealizedProfit: 0, accountType, lastPair: randomPair
      });
    } catch (err: any) {
      console.error("Robot Start Error:", err.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setFirebaseUser(null);
    } catch (err: any) {
      console.error("Sign out failed", err);
    }
  };

  const handleWithdrawal = (amount: number) => {
    console.log(`Withdrawal of $${amount} submitted.`);
  };

  if (isInitializing) {
    return (
      <div className="h-screen w-full bg-black flex flex-col items-center justify-center space-y-8 grid-bg">
        <div className="w-20 h-20 bg-[#AEB877] rounded-3xl flex items-center justify-center animate-pulse shadow-[0_0_50px_rgba(174,184,119,0.3)]">
          <span className="text-black font-black text-4xl">B</span>
        </div>
        <div className="text-center space-y-2">
          <h2 className="text-white font-black uppercase tracking-tighter text-2xl animate-pulse">{systemSettings?.siteName || 'BTCC-AI'}</h2>
          <p className="text-white/20 font-black uppercase tracking-[0.4em] text-[10px]">Advanced AI Trading System</p>
        </div>
      </div>
    );
  }

  if (!firebaseUser) {
    return (
      <>
        {publicPage === 'about' ? (
          <AboutUs 
            onBack={() => setPublicPage('landing')} 
            onGetStarted={() => setAuthModal(true)} 
            systemSettings={systemSettings} 
          />
        ) : (
          <LandingPage 
            onGetStarted={() => setAuthModal(true)} 
            onLogin={() => setAuthModal(true)} 
            systemSettings={systemSettings} 
            onAboutClick={() => setPublicPage('about')}
          />
        )}
        {authModal && <Auth onSuccess={() => setAuthModal(false)} onClose={() => setAuthModal(false)} systemSettings={systemSettings} />}
      </>
    );
  }

  if (firebaseUser && !firebaseUser.emailVerified) {
    return (
      <div className="h-screen w-full bg-black flex items-center justify-center p-8 text-center animate-in fade-in duration-500 grid-bg">
        <div className="max-w-md w-full glass-card p-12 space-y-10">
           <div className="w-24 h-24 bg-white rounded-3xl flex items-center justify-center mx-auto shadow-2xl text-black">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>
           </div>
           <div className="space-y-4">
              <h1 className="text-3xl font-black uppercase tracking-tighter">Verify_Protocol</h1>
              <p className="text-white/40 font-bold uppercase text-[9px] tracking-[0.2em] leading-relaxed">We've sent a link to <span className="text-white">{firebaseUser.email}</span>. Please verify to activate your trading terminal.</p>
           </div>
           <div className="space-y-4 pt-4">
              <button onClick={handleCheckVerification} className="w-full primary-button py-5 text-sm">I've_Verified_My_Email</button>
              <button disabled={resendingEmail} onClick={handleResendEmail} className="w-full bg-white/5 text-white/20 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:text-white transition-all">{resendingEmail ? 'Transmitting...' : 'Resend_Link'}</button>
              <button onClick={handleLogout} className="w-full text-red-500/40 hover:text-red-500 font-black uppercase tracking-widest text-[10px] transition-all pt-2">Sign_Out</button>
           </div>
        </div>
      </div>
    );
  }

  if (systemSettings?.maintenanceMode && user.role !== 'admin') {
    return (
      <div className="h-screen w-full bg-black flex items-center justify-center p-8 text-center grid-bg">
        <div className="max-w-md space-y-8">
           <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mx-auto border border-white/10 text-white/20 text-4xl">📡</div>
           <h1 className="text-5xl font-black uppercase tracking-tighter">System_Offline</h1>
           <p className="text-white/40 font-bold uppercase text-[10px] tracking-[0.3em] leading-relaxed">Neural network undergoing scheduled synchronization.</p>
           <button onClick={handleLogout} className="bg-white/5 text-white/20 px-10 py-4 rounded-2xl font-black text-[10px] uppercase hover:text-white transition-all">Sign_Out</button>
        </div>
      </div>
    );
  }

  if (user.status === 'suspended') {
    return (
       <div className="h-screen w-full bg-black flex items-center justify-center p-8 text-center grid-bg">
        <div className="max-w-md space-y-8">
           <div className="w-24 h-24 bg-red-500/10 rounded-full flex items-center justify-center mx-auto border border-red-500/20 text-red-500 text-4xl">🚫</div>
           <h1 className="text-5xl font-black uppercase tracking-tighter text-red-500">Access_Revoked</h1>
           <p className="text-white/40 font-bold uppercase text-[10px] tracking-[0.3em] leading-relaxed">Your account has been suspended by system security.</p>
           <button onClick={handleLogout} className="neo-button px-12 py-5 text-sm">Sign_Out</button>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activePage) {
      case 'chart': {
        if (!robotState.isActive) {
          return (
            <div className="flex flex-col items-center justify-center min-h-[600px] space-y-12 animate-in fade-in duration-500">
              <div className="relative">
                <div className="w-48 h-48 bg-white/5 rounded-full flex items-center justify-center border border-white/10 shadow-2xl relative">
                  <div className="absolute inset-0 bg-[#AEB877]/5 blur-[60px] rounded-full animate-pulse" />
                  <div className="w-32 h-32 bg-white rounded-[40px] flex items-center justify-center shadow-2xl relative z-10">
                    <div className="scale-[3] text-black">
                      <Icons.Robot />
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-14 h-14 bg-black border border-white/10 rounded-2xl flex items-center justify-center shadow-2xl">
                  <span className="w-3 h-3 bg-white/10 rounded-full"></span>
                </div>
              </div>
              <div className="text-center space-y-3">
                <h2 className="text-4xl font-black uppercase tracking-tighter text-white">Robot Is Idle</h2>
                <p className="text-[9px] text-white/20 font-black uppercase tracking-[0.4em]">Connection standby • No active executions</p>
              </div>
              <button 
                onClick={() => setActivePage('robot')}
                className="primary-button px-16 py-6 text-sm"
              >
                Start Trade
              </button>
            </div>
          );
        }

        const currentActivePair = robotState.isActive 
          ? (robotState.currentTrade?.pair || robotState.lastPair || selectedPair) 
          : selectedPair;
          
        return (
          <div className="flex flex-col min-h-full space-y-10 animate-in fade-in duration-500 pb-10">
            <div className="flex overflow-x-auto no-scrollbar gap-4 pb-2 shrink-0">
              {CURRENCY_PAIRS.map(p => (
                <button 
                  key={p.name} 
                  onClick={() => setSelectedPair(p.name)} 
                  className={`px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap border ${ (currentActivePair === p.name) ? 'bg-[#AEB877] text-black border-[#AEB877] shadow-2xl' : 'bg-white/5 border-white/5 text-white/20 hover:text-white' }`}
                >
                  <div className="flex items-center gap-3">
                    {p.name}
                    {robotState.isActive && (robotState.currentTrade?.pair === p.name || robotState.lastPair === p.name) && (
                      <span className="w-2 h-2 bg-black rounded-full animate-pulse"></span>
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div className="flex-1 min-h-[600px]">
              <ChartWidget pair={currentActivePair} />
            </div>
          </div>
        );
      }
      case 'deals': return <Deals deals={deals} totalNetworkPayout={totalNetworkPayout} />;
      case 'robot': return user.botActive ? ( <ActiveRobot state={robotState} onStop={handleStopRobot} /> ) : ( <RobotSetup onStart={handleStartRobot} accountType={activeAccountType} setActiveAccountType={setActiveAccountType} balance={activeAccountType === AccountType.DEMO ? user.demoBalance : user.liveBalance} systemSettings={systemSettings} /> );
      case 'support': return <Support systemSettings={systemSettings} />;
      case 'account': return <Account user={user} onLogout={handleLogout} onWithdrawal={handleWithdrawal} onDepositClick={() => setDepositModal(true)} initialView={accountInitialView} systemSettings={systemSettings} />;
      case 'admin': return user.role === 'admin' ? <AdminPanel user={user} /> : <ChartWidget pair="BTC/USDT" />;
      case 'about': return <AboutUs onBack={() => setActivePage('chart')} onGetStarted={() => {}} systemSettings={systemSettings} />;
      default: return <ChartWidget pair="BTC/USDT" />;
    }
  };

  const handlePageChange = (page: string, initialView?: any) => {
    setAccountInitialView(initialView);
    setActivePage(page);
  };

  return (
    <>
      <Layout activePage={activePage} setActivePage={handlePageChange} user={user} activeAccountType={activeAccountType} setActiveAccountType={setActiveAccountType} onDepositClick={() => setDepositModal(true)} systemSettings={systemSettings}>{renderContent()}</Layout>
      {depositModal && <DepositModal onClose={() => setDepositModal(false)} />}
      
      {showInstallBanner && (
        <div className="fixed bottom-24 left-6 right-6 md:left-auto md:right-12 md:w-96 z-[1000] animate-in slide-in-from-bottom-10 duration-700">
          <div className="glass-card p-8 shadow-2xl border border-[#AEB877]/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#AEB877]/5 rounded-full blur-[40px] -mr-16 -mt-16"></div>
            
            {showIOSInstructions ? (
              <div className="relative z-10 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#AEB877] rounded-xl flex items-center justify-center shadow-xl shrink-0">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="3"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
                  </div>
                  <h3 className="text-white font-black uppercase tracking-tight text-lg">iOS_Install</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-white/60 text-[10px] font-black uppercase tracking-widest">
                    <span className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center border border-white/10">1</span>
                    <span>Tap the 'Share' icon in Safari</span>
                  </div>
                  <div className="flex items-center gap-4 text-white/60 text-[10px] font-black uppercase tracking-widest">
                    <span className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center border border-white/10">2</span>
                    <span>Scroll down and select 'Add to Home Screen'</span>
                  </div>
                </div>
                <button onClick={() => setShowInstallBanner(false)} className="w-full bg-white text-black py-4 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-[#AEB877] transition-all shadow-xl">Got It</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-6 relative z-10">
                  <div className="w-16 h-16 bg-[#AEB877] rounded-2xl flex items-center justify-center shadow-xl shrink-0">
                    <span className="text-black font-black text-2xl">B</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-black uppercase tracking-tight text-lg">Install Our App</h3>
                    <p className="text-white/40 text-[10px] font-black uppercase tracking-widest mt-1 leading-relaxed">Secure your node as a desktop/mobile application user for 4ms execution.</p>
                  </div>
                </div>
                <div className="flex gap-4 mt-8 relative z-10">
                  <button onClick={handleInstallClick} className="flex-1 bg-white text-black py-4 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-[#AEB877] transition-all shadow-xl">Install App</button>
                  <button onClick={() => setShowInstallBanner(false)} className="px-6 py-4 bg-white/5 text-white/20 rounded-xl font-black uppercase text-[10px] tracking-widest hover:text-white transition-all">Dismiss</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default App;